import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, MessageCircle } from "lucide-react";
import BiodealLogo from "./BiodealLogo";
import {
  BAH_ADDRESS_LINES,
  BAH_EMAIL,
  BAH_PHONE_DISPLAY,
  BAH_WHATSAPP_DISPLAY,
  buildWaLink,
  WA_TEMPLATES,
} from "@/lib/whatsapp";

const COL_PAGES = [
  { label: "Vet Support Hub", to: "/vet-support-hub" },
  { label: "Animal Health Solutions", to: "/solutions" },
  { label: "Products", to: "/products" },
  { label: "Veterinary Instruments", to: "/instruments" },
  { label: "Cold Chain & Vaccine Support", to: "/cold-chain" },
];

const COL_BUSINESS = [
  { label: "Training & CMT", to: "/training" },
  { label: "Become a Stockist", to: "/become-a-stockist" },
  { label: "Distribution / Institutional", to: "/distribution" },
  { label: "About", to: "/about" },
  { label: "Contact / WhatsApp Support", to: "/contact" },
];

export default function Footer() {
  return (
    <footer className="bg-bah-ink text-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
          <div className="md:col-span-5">
            <BiodealLogo size="lg" />
            <p className="mt-6 font-display text-2xl leading-snug text-white max-w-md">
              Responsible Animal Health. Trusted by Professionals. Built for
              Farmers.
            </p>
            <p className="mt-4 text-sm text-white/70 max-w-md leading-relaxed">
              Biodeal Animal Health is part of Biodeal Laboratories Limited —
              supporting veterinarians, agrovets, livestock producers and
              animal caregivers across East Africa.
            </p>
            <a
              href={buildWaLink(WA_TEMPLATES.technical_general + " ")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] text-bah-ink font-bold px-5 py-3 text-sm hover:bg-[#1FBE5A] transition-colors"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp Technical Team
            </a>
          </div>

          <div className="md:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.22em] text-bah-yellow font-bold">
              Platform
            </div>
            <ul className="mt-4 space-y-2.5 text-sm text-white/85">
              {COL_PAGES.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="hover:text-bah-yellow transition-colors"
                    data-testid={`footer-link-${l.to.replace(/\//g, "")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-[11px] uppercase tracking-[0.22em] text-bah-yellow font-bold">
              Business
            </div>
            <ul className="mt-4 space-y-2.5 text-sm text-white/85">
              {COL_BUSINESS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="hover:text-bah-yellow transition-colors"
                    data-testid={`footer-business-${l.to.replace(/\//g, "")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-[11px] uppercase tracking-[0.22em] text-bah-yellow font-bold">
              Contact
            </div>
            <ul className="mt-4 space-y-3 text-sm text-white/85">
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 text-bah-yellow shrink-0" />
                <div className="leading-relaxed">
                  {BAH_ADDRESS_LINES.map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </div>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-bah-yellow shrink-0" />
                <a
                  href={`tel:+254706333829`}
                  className="hover:text-bah-yellow"
                  data-testid="footer-phone"
                >
                  {BAH_PHONE_DISPLAY}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-bah-yellow shrink-0" />
                <a
                  href={buildWaLink("Hello Biodeal Animal Health")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-bah-yellow"
                  data-testid="footer-whatsapp-link"
                >
                  {BAH_WHATSAPP_DISPLAY}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-bah-yellow shrink-0" />
                <a
                  href={`mailto:${BAH_EMAIL}`}
                  className="hover:text-bah-yellow break-all"
                  data-testid="footer-email"
                >
                  {BAH_EMAIL}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-white/60">
          <div>
            © {new Date().getFullYear()} Biodeal Laboratories Limited — Biodeal
            Animal Health. All rights reserved.
          </div>
          <div className="flex items-center gap-2">
            <span>For veterinary use only. Use as directed by a qualified veterinarian.</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
